import type {
  PaymentAction,
  ResultCode,
  PaymentMethod,
  GooglePayConfiguration,
  ApplePayConfiguration,
  ActionHandledReturnObject,
  AdditionalDetailsActions,
  ThreeDS2ChallengeConfiguration,
} from '@adyen/adyen-web';
import type { ProviderEnum } from './common';
import type { AppchargeErrorType, FinalizeStatus, PayFromExpressCheckout } from './payment';
import type { Customer, Pricing } from './checkout';
import type { FeatureFlag } from '@/constants/featureFlags';

export enum AdyenInputType {
  SHOPPER_EMAIL = 'shopperEmail',
  SHOPPER_NAME = 'shopperName',
  BILLING_ADDRESS = 'billingAddress',
  TELEPHONE_NUMBER = 'telephoneNumber',
  EMAIL = 'email',
  ZIP_CODE = 'zipCode',
}

export interface AdyenConfig {
  clientKey: string;
  env: 'test' | 'live';
}

export interface CardDetails {
  bin: string;
  endDigits: string;
  expiryDate: string;
  cardType?: string;
  paymentMethodData?: Record<string, unknown>;
  browserInfo?: Record<string, unknown>;
}

export interface HandlePayParams {
  email: string;
  cardholderName: string;
  shouldSaveCard: boolean;
}

export interface AdyenCheckoutInstance {
  components: {
    card?: (config: Record<string, unknown>) => AdyenCardComponent;
  };
  createFromAction: (action: unknown, config: Record<string, unknown>) => unknown;
}

export interface AdditionalDetailsData {
  data: {
    details: {
      redirectResult?: string;
      threeDSResult?: string;
      [key: string]: unknown;
    };
    paymentData?: string;
    sessionData?: string;
  };
}

export interface AdyenActionComponent {
  resolve: (data: unknown) => void;
  reject: () => void;
}

export type ChallengeWindowSize = NonNullable<ThreeDS2ChallengeConfiguration['challengeWindowSize']>;

export interface CallbackActions {
  // `actions` is optional because it depends on the SDK version: the pinned
  // 6.23.0 calls createFromAction handlers with two arguments only, while
  // >= 6.25.1 supplies `(state, component, actions)` and requires settling
  // (adyen-web#3589). The checkout settles the payment through its own flow
  // either way.
  onAdditionalDetails: (
    state: AdditionalDetailsData,
    component: AdyenActionComponent,
    actions?: AdditionalDetailsActions
  ) => Promise<void>;
  // ACDEV-26410: the 3DS2 component only tells us the challenge iframe actually
  // rendered through `onActionHandled`, and only reports component-level
  // failures (missing paymentData, unusable action) through `onError`. Both are
  // optional in the SDK, so leaving them unwired makes a challenge that never
  // paints indistinguishable from a shopper who walked away.
  onActionHandled?: (rtnObj: ActionHandledReturnObject) => void;
  onError?: (error: unknown) => void;
}

export interface AdyenCardComponent {
  mount: (element: HTMLElement | string) => void;
  unmount: () => void;
  submit: () => void;
  showValidation: () => void;
  setFocusOn: (field: string) => void;
  isValid: boolean;
}

export interface AdyenUIElement {
  mount: (element: string) => void;
}

export interface CardFieldEvent {
  fieldType: string;
  valid: boolean;
  endDigits?: string;
  expiryDate?: string;
}

export interface BinValueEvent {
  binValue: string;
  brand?: string;
}

export interface AdyenFinalizePaymentResponse {
  paymentProvider: ProviderEnum.ADYEN;
  adyen: {
    message: string;
    resultCode: ResultCode;
    status: FinalizeStatus;
    action?: PaymentAction;
    redirectUrl?: string;
  };
  orderId: string;
  appChargeErrorType?: AppchargeErrorType;
}

export interface AdyenGooglePayHandlers {
  onClick?: GooglePayConfiguration['onClick'];
  onAuthorized?: GooglePayConfiguration['onAuthorized'];
  onError?: GooglePayConfiguration['onError'];
  onPaymentCompleted?: GooglePayConfiguration['onPaymentCompleted'];
  onPaymentFailed?: GooglePayConfiguration['onPaymentFailed'];
  onSubmit?: GooglePayConfiguration['onSubmit'];
  // ACDEV-25067: dynamic in-sheet tax/price update. Lives outside `handlers`
  // proper on the Google Pay config (`paymentDataCallbacks.onPaymentDataChanged`),
  // so `getGooglePayComponentOptions` nests it rather than spreading it.
  onPaymentDataChanged?: NonNullable<GooglePayConfiguration['paymentDataCallbacks']>['onPaymentDataChanged'];
}

export interface CreateGooglePayComponentOptions {
  configuration: { gatewayMerchantId: string } & Record<string, unknown>;
  transactionInfo: {
    displayItems: unknown[];
    countryCode: string;
    currencyCode: string;
    totalPriceStatus: 'FINAL' | 'ESTIMATED' | 'NOT_CURRENTLY_KNOWN';
    totalPrice: string;
    totalPriceLabel: string;
  };
  handlers: AdyenGooglePayHandlers;
}

export interface AdyenGooglePayElementOptionsParams {
  gatewayMerchantId: string;
  googlePayConfiguration: Record<string, unknown>;
  submitParamsRef: { current: { email: string } };
  customer: Customer | null;
  handleError: (err?: unknown) => void;
  isPaymentInProgress: { current: boolean };
  runPayment: (
    params?: unknown,
    payFromExpressCheckout?: PayFromExpressCheckout,
    provider?: ProviderEnum
  ) => Promise<unknown>;
  pricing: Pricing;
}

export interface CreateGooglePayComponentParams {
  googlePayPaymentMethod: {
    publicConfig?: unknown;
    paymentMethodConfig?: { configuration?: Record<string, unknown> };
  };
  customer: Customer | null;
  pricing: Pricing;
  handlers: AdyenGooglePayHandlers;
  // ACDEV-25067: needed by `getGooglePayComponentOptions` to gate the
  // wallet-ZIP-tax shipping config on `WALLET_ZIP_TAX`.
  featureFlags: Record<FeatureFlag, boolean>;
  // ACDEV-27914: paired with `featureFlags` inside `shouldUseWalletDerivedZipTax`
  // — a session that started without a resolved US/CA tax activates the
  // wallet-tax shipping config regardless of the FF's actual value.
  sessionStartedWithoutValidTax: boolean;
}

export interface AdyenGooglePayConfig {
  clientKey: string;
  environment?: 'test' | 'live';
  env?: string;
  merchantId?: string;
}

export interface AdyenGooglePayPaymentMethod extends Omit<PaymentMethod, 'publicConfig'> {
  publicConfig?: AdyenGooglePayConfig;
  paymentMethodConfig?: { type?: string; configuration?: Record<string, unknown> };
}

export interface AdyenApplePayHandlers {
  onClick?: ApplePayConfiguration['onClick'];
  onAuthorized?: ApplePayConfiguration['onAuthorized'];
  onError?: ApplePayConfiguration['onError'];
  onPaymentCompleted?: ApplePayConfiguration['onPaymentCompleted'];
  onPaymentFailed?: ApplePayConfiguration['onPaymentFailed'];
  onSubmit?: ApplePayConfiguration['onSubmit'];
  // ACDEV-25067: dynamic in-sheet tax/price update. Apple fires this with the
  // selected card's redacted `billingContact` (postal code) before authorization
  // — including on sheet open for the default card — so we can recompute tax
  // and resolve with the updated total + line items. This replaced the earlier
  // `onShippingContactSelected` wiring so the sheet no longer renders a
  // "Shipping" section for digital-goods purchases.
  onPaymentMethodSelected?: ApplePayConfiguration['onPaymentMethodSelected'];
}

export interface AdyenApplePayConfig {
  clientKey: string;
  environment?: 'test' | 'live';
  env?: string;
}

export interface AdyenApplePayPaymentMethod extends Omit<PaymentMethod, 'publicConfig'> {
  publicConfig?: AdyenApplePayConfig;
  paymentMethodConfig?: { type?: string; configuration?: Record<string, unknown> };
}

export interface CreateApplePayComponentParams {
  applePayPaymentMethod: {
    publicConfig?: unknown;
    paymentMethodConfig?: { configuration?: Record<string, unknown> };
  };
  customer: Customer | null;
  pricing: Pricing;
  handlers: AdyenApplePayHandlers;
  // ACDEV-25067: needed by `getApplePayComponentOptions` to gate the
  // wallet-ZIP-tax shipping config on `WALLET_ZIP_TAX`.
  featureFlags: Record<FeatureFlag, boolean>;
  // ACDEV-27914: paired with `featureFlags` inside `shouldUseWalletDerivedZipTax`
  // — a session that started without a resolved US/CA tax activates the
  // wallet-tax billing config regardless of the FF's actual value.
  sessionStartedWithoutValidTax: boolean;
  // ACDEV-26742: display-only company name for the Apple Pay sheet's
  // grand-total row label (gated by APPLE_PAY_COMPANY_NAME_LABEL).
  // Local to the PassKit sheet — never sent to Adyen or the bank; the bank
  // statement stays driven by the BE `shopperStatement` / Customer Area
  // dynamic-value config (docs.adyen.com/account/transaction-description).
  companyName?: string;
}

export interface ApplePayPaymentDetailsRef {
  email: string;
  paymentMethodDetails: Record<string, unknown>;
}
